/**
 * The geodata the vendored renderers ask a CDN for, served from the bundle.
 *
 * Lines 12–13 of the vendored renderer are two hard-coded jsdelivr URLs, awaited
 * in `boot()`:
 *
 * ```js
 * const ATLAS  = 'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-50m.json';
 * const MARINE = 'https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@…';
 * const [world, marine] = await Promise.all([d3.json(ATLAS), d3.json(MARINE)]);
 * ```
 *
 * Phase 0 bundled `world-atlas` from npm and stated that no CDN reference is left
 * in the build (Q17: the room may be air-gapped). That stops being true the moment
 * this element mounts — and **Q23 excepted terrain only**, deliberately and in
 * writing. Two hits per session against a package registry is not the accepted
 * exception; it is the whole basemap, and a console that draws no coastline at
 * all in a room without a route to jsdelivr is not a degradation anybody
 * accepted.
 *
 * ## How it is resolved, and why not by editing the file
 *
 * M5-D7 keeps the vendored renderers byte-identical to their source so a
 * re-vendor stays a clean diff, so changing those two lines is the wrong shape:
 * every future transfer would land as a conflict on a line somebody has to
 * remember to re-edit. But the renderer does not fetch — it asks **`window.d3`**
 * to fetch, and `window.d3` is ours to construct (`maps/vendor.ts`). So the d3
 * handed to it resolves those two urls locally and passes everything else
 * through. The renderer is unchanged, the request never leaves the page, and the
 * seam is one named function rather than a patch.
 *
 * ## The marine index is empty, and the pack test is what enforces it
 *
 * `world-atlas` is on npm and is bundled. The Natural Earth marine polygons are
 * not a package — the prototype pulls one file out of a GitHub repository, and
 * the npm mirror of that repository is the whole of Natural Earth. So the marine
 * index the renderer builds is empty here, and a pack entry naming a `sea:`
 * source would resolve to nothing (`buildRings` `console.warn`s and carries on
 * with a zone that has no polygons).
 *
 * That is not left to chance: `test/mappack.test.ts` asserts every source in
 * every shipped pack resolves against the index this module actually serves, so
 * a sea zone fails in CI rather than on a projector. First Contact's four zones
 * are land, which is also what its order of battle needs — every one of its
 * regions holds ground assets.
 */

/**
 * ## Phase 6 added a third, out of the same package
 *
 * `orbital-map.js` line 404 fetches `countries-110m.json` for the globe at the
 * centre of the board — a coarser cut of the atlas already bundled here, from
 * the *same* `world-atlas@2.0.2` package, so it costs one entry in this table
 * and nothing else. The vendored file is untouched by it, for the reason above:
 * it does not fetch, it asks `window.d3` to.
 */

export const ATLAS_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-50m.json';
export const EARTH_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json';
export const MARINE_URL =
  'https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@v5.1.2/geojson/ne_50m_geography_marine_polys.geojson';

/** The named marine polygons available to a pack. See the header: none, today. */
export const MARINE = Object.freeze({ type: 'FeatureCollection', features: [] });

/**
 * Natural Earth 50m countries, as TopoJSON.
 *
 * Dynamically imported so the 756 kB of geometry rides in the map chunk rather
 * than the shell's, and is fetched once, when a map tab is first opened. 50m
 * rather than 110m because 50m is what the renderer's own url names, and holding
 * to that keeps "serve what it asked for" a rule rather than a judgement call.
 */
async function atlas(): Promise<unknown> {
  const module = await import('world-atlas/countries-50m.json');
  return module.default;
}

/**
 * The same atlas at 110m, which is the cut `orbital-map.js` names.
 *
 * A globe 60 pixels across does not want 756 kB of coastline, and holding to the
 * resolution each renderer asked for keeps "serve what it asked for" a rule
 * rather than a judgement call — the two share nothing but a package.
 */
async function earth(): Promise<unknown> {
  const module = await import('world-atlas/countries-110m.json');
  return module.default;
}

/**
 * The local answer for one of the renderer's two urls, or `null` for anything
 * else — which `maps/vendor.ts` then lets d3 fetch normally.
 */
export function localGeodata(url: string): Promise<unknown> | null {
  if (url === ATLAS_URL) return atlas();
  if (url === EARTH_URL) return earth();
  if (url === MARINE_URL) return Promise.resolve(MARINE);
  return null;
}
